import { useState, useMemo } from "react";
import { Search, X } from "lucide-react";

interface Item {
  id: string;
  title?: string;
  name?: string;
  slug?: string;
  featuredImage?: string;
}

interface RelatedItemsPickerProps {
  items: Item[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  label?: string;
  placeholder?: string;
}

export default function RelatedItemsPicker({
  items,
  selectedIds,
  onChange,
  label = "Related Items",
  placeholder = "Search items...",
}: RelatedItemsPickerProps) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(
      (item) =>
        (item.title || item.name || "").toLowerCase().includes(q) ||
        (item.slug || "").toLowerCase().includes(q),
    );
  }, [items, search]);

  const selectedItems = items.filter((item) => selectedIds.includes(item.id));
  const availableItems = filtered.filter((item) => !selectedIds.includes(item.id));

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((i) => i !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</h3>

      {/* Selected items */}
      {selectedItems.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Selected ({selectedItems.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedItems.map((item) => (
              <span
                key={item.id}
                className="bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium"
              >
                {item.title || item.name}
                <button
                  type="button"
                  onClick={() => toggle(item.id)}
                  className="hover:bg-primary-200 dark:hover:bg-primary-800 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input pl-9"
          placeholder={placeholder}
        />
      </div>

      {/* Available items */}
      <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-gray-200 p-2 dark:border-gray-700">
        {availableItems.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-400">
            {search ? "No matching items" : "No items available"}
          </p>
        ) : (
          availableItems.map((item) => (
            <label
              key={item.id}
              className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <input
                type="checkbox"
                checked={false}
                onChange={() => toggle(item.id)}
                className="text-primary-600 focus:ring-primary-500 h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {item.title || item.name}
              </span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}
